import {
  useQuery,
  useMutation,
  useQueryClient,
  useQueries,
} from '@tanstack/react-query';
import Axios from '@/lib/axiosConfig';
import { useAuth } from '@/context/Auth';
import { toast } from 'sonner';
import type {
  Module,
  ContentFlagsListParams,
  ContentFlagsListResponse,
  ReportedItemType,
} from './types/contentFlags';
import { useIsPublic } from '@/context/PublicMode';

const getEndpointForModule = (
  module: Module,
  reportedItemType?: ReportedItemType
): string => {
  switch (module) {
    case 'forums':
      return '/posts/reports';
    case 'groups':
      return '/groups/reports';
    case 'businesses':
      return `/businesses/reports`;
    case 'members':
      return '/users/reports';
    case 'messages':
      return '/chats/reports';
    case 'marketplace':
      switch (reportedItemType) {
        case 'listing':
          return '/marketplace/listings/reports';
        case 'provider':
          return '/marketplace/service-providers/reports';
        case 'request':
          return '/marketplace/service-providers/requests/reports';
        default:
          if (!reportedItemType) {
            return '/marketplace/reports';
          }
          throw new Error(
            `Unsupported reported item type for update: ${reportedItemType}`
          );
      }
    default:
      throw new Error(`Unknown module: ${module}`);
  }
};

export const useContentFlagsQuery = ({
  searchText = '',
  status,
  module,
  sortBy = 'createdAt',
  reason = '',
  order = 'DESC',
  page = 1,
  pageSize = 10,
}: ContentFlagsListParams & { module: Module }) => {
  const { getAPIRequestTokenHeaders } = useAuth();
  const endpoint = getEndpointForModule(module);
  const offset = (page - 1) * pageSize;
  const isPublic = useIsPublic();
  return useQuery<ContentFlagsListResponse>({
    queryKey: [
      'content-flags',
      module,
      searchText,
      status,
      reason, 
      sortBy,
      order,
      page,
      pageSize,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (searchText) params.append('searchText', searchText);
      if (status && status !== 'all') params.append('status', status);
      if (reason) params.append('reason', reason); 
      params.append('sortBy', sortBy);
      params.append('order', order);
      params.append('offset', offset.toString());
      params.append('limit', pageSize.toString());

      const url = `${endpoint}?${params.toString()}`;

      const { data } = await Axios.get<any>(url, {
        headers: getAPIRequestTokenHeaders(),
      });
      return data.responseObject;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    enabled : !isPublic,
  });
};

export const useAllPendingCountsQuery = (): {
  data: Record<Module, number> | undefined;
  isLoading: boolean;
} => {
  const modules: Module[] = ['forums', 'groups', 'businesses', 'marketplace', 'members', 'messages'];
  const { getAPIRequestTokenHeaders } = useAuth();

  const results = useQueries({
    queries: modules.map((module) => {
      const endpoint = getEndpointForModule(module);
      return {
        queryKey: ['content-flags-pending-count', module],
        queryFn: async () => {
          const url = `${endpoint}?status=0&limit=1`;
          const { data } = await Axios.get<any>(url, {
            headers: getAPIRequestTokenHeaders(),
          });
          return { module, count: data.responseObject.count as number };
        },
        staleTime: 5 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
      };
    }),
  });

  const isLoading = results.some((result) => result.isLoading);

  const data = isLoading
    ? undefined
    : results.reduce((acc, result) => {
        if (result.data) {
          acc[result.data.module] = result.data.count;
        }
        return acc;
      }, {} as Record<Module, number>);

  return { data, isLoading };
};

export const useUpdateFlagStatus = () => {
  const queryClient = useQueryClient();
  const { getAPIRequestTokenHeaders } = useAuth();

  return useMutation({
    mutationFn: async ({
      module,
      flagIDs,
      newStatus,
      reportedItemType,
    }: {
      module: Module;
      flagIDs: string[];
      // 1 accept, 2 reject (content) / reject report (members),
      // 3 temp-suspend, 4 permanently-suspend (members only).
      newStatus: 1 | 2 | 3 | 4;
      reportedItemType?: ReportedItemType;
    }) => {
      const endpoint = getEndpointForModule(module, reportedItemType);

      const statusString = newStatus.toString();
      const payload = {
        status: statusString,
        reportIDs: flagIDs,
      };

      const { data } = await Axios.put(endpoint, payload, {
        headers: getAPIRequestTokenHeaders(),
      });
      return data;
    },
    onSuccess: (_, variables) => {
      const actionLabels: Record<1 | 2 | 3 | 4, string> = {
        1: 'Accepted',
        2: variables.module === 'messages' ? 'Declined' : 'Rejected',
        3: variables.module === 'messages' ? 'Warned' : 'Temporarily suspended',
        4: 'Permanently suspended',
      };
      const action = actionLabels[variables.newStatus];
      toast.success(
        `${action} ${variables.flagIDs.length} report(s) successfully!`
      );
      queryClient.invalidateQueries({
        queryKey: ['content-flags', variables.module],
      });
      queryClient.invalidateQueries({
        queryKey: ['content-flags-pending-count'],
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message ||
        'Failed to update report status. Please try again.';
      toast.error(errorMessage);
    },
    retry: false,
  });
};
